import { Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { UsersService } from '@treasury/domain/backoffice/services';
import { CompanyUserSummary } from '@treasury/domain/company/user/entities';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer';
import { TmToastService } from '@treasury/presentation/services/toast';

import {
    ApproveOrRejectCompanyUserCommand,
    CompanyUserActionType,
    ConsumerViewUserCommand,
    UserActionFlags,
} from '../';
import { RowActionCommandResult } from '../../base.cmd';
import { LegacyResetPasswordCommand } from '../../shared/users/commands/legacyResetPassword.cmd';
import {
    CompanyUserActionRecord,
    SharedUsersActionRecord,
    SharedUsersActionType,
} from '../../shared/users/users-actions.types';
import { Decision } from '../approveOrReject/approve-or-reject-drawer/approve-or-reject.component';

@Injectable()
export class ConsumerCompanyUserActionHelper {
    constructor(
        private readonly entitlementsService: EntitlementsService,
        drawerService: TmSideDrawerService,
        usersService: UsersService,
        toastService: TmToastService,
        navigationService: NavigationService
    ) {
        this.legacyResetPasswordCommand = new LegacyResetPasswordCommand(
            drawerService,
            usersService,
            toastService
        );
        this.viewUserCommand = new ConsumerViewUserCommand(navigationService);
        this.approveOrRejectCommand = new ApproveOrRejectCompanyUserCommand();
    }

    @InjectProperty({ async: true, token: FeatureFlagService })
    declare private readonly featureFlagServicePromise: Promise<FeatureFlagService>;

    private readonly legacyResetPasswordCommand: LegacyResetPasswordCommand<CompanyUserSummary>;

    private readonly viewUserCommand: ConsumerViewUserCommand;

    private readonly approveOrRejectCommand: ApproveOrRejectCompanyUserCommand;

    private _userActions: RowActionCommandResult<CompanyUserSummary, CompanyUserActionType>[] = [];

    private _flags: UserActionFlags = new UserActionFlags();

    /**
     * Gets the company user actions.
     *
     * @returns An array of company user actions.
     */
    public get userActions() {
        return this._userActions;
    }

    /**
     * Retrieves a list of company user actions with their respective properties.
     *
     * This method generates a list of user actions.
     * Each user action is then mapped to an object containing the following properties:
     * - `id`: The identifier of the user action.
     * - `label`: The label of the user action.
     * - `hiddenCondition`: A boolean indicating if the user action is hidden.
     *
     * @returns {Promise<void>}
     *          A promise that resolves when the user actions have been loaded.
     */
    public async loadCompanyUserActions(): Promise<void> {
        const featureFlagService = await this.featureFlagServicePromise;
        this._flags = {
            isUisEnabled: await featureFlagService.isEnabled(Feature.UisEnabled),
            canResetUserPassword: await this.entitlementsService.isEnabled(
                Entitlements.ResetUserPassword
            ),
        };

        const actionConfigurations = Object.entries(this.generateCompanyUserActionConfig());

        this._userActions = actionConfigurations.map(([type, value]) => {
            return {
                type: type as CompanyUserActionType,
                title: value.title,
                hiddenCondition: value.hiddenCondition || false,
                disabledCondition: value.disabledCondition || false,
                action: value.action,
            };
        });
    }

    private notImplemented() {
        alert('Not Implemented');
    }

    private generateCompanyUserActionConfig():
        | CompanyUserActionRecord
        | SharedUsersActionRecord<CompanyUserSummary> {
        const { isUisEnabled, canResetUserPassword } = this._flags;
        return {
            [SharedUsersActionType.LegacyResetPassword]: this.legacyResetPasswordCommand.command(
                isUisEnabled,
                canResetUserPassword
            ),
            [CompanyUserActionType.Approve]: this.approveOrRejectCommand.command(Decision.Approve),
            [CompanyUserActionType.Reject]: this.approveOrRejectCommand.command(Decision.Reject),
            [CompanyUserActionType.Edit]: {
                type: CompanyUserActionType.Edit,
                title: 'Edit User',
                hiddenCondition: user => !user.canEdit,
                action: this.notImplemented,
            },
            [CompanyUserActionType.Copy]: {
                type: CompanyUserActionType.Copy,
                title: 'Copy User',
                hiddenCondition: user => !user.canCopy,
                action: this.notImplemented,
            },
            [CompanyUserActionType.View]: this.viewUserCommand.command(),
        };
    }
}
